import axios from 'axios'

const AUTH_TOKEN_KEY = 'punchApiToken'

const api = axios.create({
  baseURL: '',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

export const setApiAuthToken = (token: string | null) => {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token)
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY)
  }
}

export const getApiAuthToken = () => localStorage.getItem(AUTH_TOKEN_KEY)

// Selected tenant context for platform admins (system super admin / platform
// admin). When set, tenant-scoped requests carry x-tenant-id so the backend
// acts on the explicitly selected tenant instead of guessing between multiple
// tenants. Managed by AccessProvider.
let platformTenantId: string | null = null

export const setApiTenantContext = (tenantId: string | null) => {
  platformTenantId = tenantId || null
}

export const getApiTenantContext = () => platformTenantId

// Pre-auth routes and intentionally platform-global routes must not have the
// selected tenant injected.
const isTenantHeaderExempt = (url: string) => {
  const path = url.split('?')[0]
  return (
    path.startsWith('/auth/')
    || path.startsWith('/api/auth/')
    || path === '/api/system/tenants'
    || path === '/api/system/organizations'
    || path === '/api/system/organization-relationships'
    || path === '/api/system/users'
    || path.startsWith('/api/system/lifecycle/')
  )
}

// Add loading state management if needed
let requestCount = 0

api.interceptors.request.use((config) => {
  requestCount++
  const token = getApiAuthToken()
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`
  }
  if (platformTenantId && !config.headers['x-tenant-id'] && !isTenantHeaderExempt(config.url || '')) {
    config.headers['x-tenant-id'] = platformTenantId
  }
  const loadingBar = document.getElementById('globalLoadingBar')
  if (loadingBar) {
    loadingBar.style.display = 'block'
  }
  return config
})

api.interceptors.response.use(
  (response) => {
    requestCount--
    if (requestCount === 0) {
      const loadingBar = document.getElementById('globalLoadingBar')
      if (loadingBar) {
        loadingBar.style.display = 'none'
      }
    }
    return response
  },
  (error) => {
    requestCount--
    if (requestCount === 0) {
      const loadingBar = document.getElementById('globalLoadingBar')
      if (loadingBar) {
        loadingBar.style.display = 'none'
      }
    }
    return Promise.reject(error)
  }
)

export default api
